import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ErroAPI, api, data, dataHora, desde, gb, uptime } from "../api.ts";
import { Cartao, Medidor, SeloStatus, Vazio } from "../componentes/basicos.tsx";
import { FaixaPresenca, SerieInstancias } from "../componentes/serie.tsx";
import { ehAdmin, podeEscrever, useSessao } from "../sessao.tsx";
import { useDados } from "../usarDados.ts";

type Resposta = {
  maquina: {
    id: number; licenca_id: number; hostname: string; apelido: string;
    fingerprint: string; status: string; motivo_status: string;
    ip_local: string; ip_publico: string; ativada_em: string;
    ultimo_contato: string | null; empresa: string; licenca_apelido: string;
    telemetria: {
      maquina?: { hostname?: string; usuario_windows?: string; dominio?: string };
      sistema?: Record<string, unknown>;
      aplicacao?: Record<string, unknown>;
    };
  };
  historico: {
    id: number; recebido_em: string; acao: string; versao_app: string;
    instancias: number | null; instancia_ativa: string; disco_livre_mb: number | null;
    ip_publico: string; ip_local: string; uptime_s: number | null;
    hostname: string; usuario_win: string; dominio: string;
    so_versao: string; arquitetura: string; cpu_nucleos: number | null;
    ram_mb: number | null; ultimo_erro: string;
  }[];
  diario: { dia: string; total: number; instancias_max: number }[];
  licenca: { max_instancias: number; tolerancia_offline_h: number; modo_offline: string };
};

export function Maquina() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const { dados: r, erro, carregando, recarregar } = useDados<Resposta>(`/maquinas/${id}`);
  const [falha, setFalha] = useState("");
  const [aviso, setAviso] = useState("");
  const [aberta, setAberta] = useState<number | null>(null);

  async function acao(fn: () => Promise<{ mensagem?: string }>) {
    setFalha(""); setAviso("");
    try {
      const res = await fn();
      if (res?.mensagem) setAviso(res.mensagem);
      recarregar();
    } catch (e) {
      setFalha(e instanceof ErroAPI ? e.message : "Nao consegui concluir.");
    }
  }

  if (carregando) return <div className="carregando">carregando...</div>;
  if (erro || !r) return <div className="alerta alerta--erro">{erro}</div>;

  const m = r.maquina;
  const sis = (m.telemetria?.sistema ?? {}) as Record<string, number | string>;
  const app = (m.telemetria?.aplicacao ?? {}) as Record<string, unknown>;
  const maq = m.telemetria?.maquina ?? {};
  const ultimoErro = String(app["ultimo_erro"] ?? "");
  const instancias = Array.isArray(app["instancias"]) ? (app["instancias"] as string[]) : [];

  return (
    <>
      <div className="entre" style={{ marginBottom: "var(--e-lg)" }}>
        <div>
          <h1 className="mb-0">{m.hostname || `Maquina ${m.id}`}</h1>
          <span className="suave pequeno">{m.empresa} &middot; {m.licenca_apelido}</span>
        </div>
        <div className="linha">
          <SeloStatus status={m.status} />
          <Link className="botao botao--secundario" to={`/licencas/${m.licenca_id}`}>Ver licenca</Link>
        </div>
      </div>

      {aviso && <div className="alerta alerta--sucesso">{aviso}</div>}
      {falha && <div className="alerta alerta--erro">{falha}</div>}
      {m.motivo_status && (
        <div className="alerta alerta--aviso">
          <span className="alerta__titulo">Motivo</span>{m.motivo_status}
        </div>
      )}

      {/* A faixa de erro no topo e o que converte esta pagina de ficha em
          ferramenta de suporte. O texto do erro E o valor. */}
      {ultimoErro && (
        <div className="alerta alerta--erro">
          <span className="alerta__titulo">
            Ultimo erro reportado{app["ultimo_erro_em"] ? ` (${desde(String(app["ultimo_erro_em"]))})` : ""}
          </span>
          <span className="mono pequeno">{ultimoErro}</span>
        </div>
      )}

      <div className="grade grade--3">
        <Cartao titulo="Maquina">
          <dl className="perfil">
            <dt>Hostname</dt><dd>{m.hostname || "--"}</dd>
            <dt>Usuario Windows</dt><dd>{maq.usuario_windows || "--"}</dd>
            <dt>Dominio</dt><dd>{maq.dominio || <span className="apagado">fora de dominio</span>}</dd>
            <dt>Digital</dt><dd className="mono pequeno">{m.fingerprint.replace(/^sha256:/, "").slice(0, 16)}</dd>
            <dt>IP local</dt><dd>{m.ip_local || "--"}</dd>
            <dt>IP publico</dt><dd>{m.ip_publico || "--"}</dd>
            <dt>Ativada em</dt><dd>{data(m.ativada_em)}</dd>
            <dt>Ultimo contato</dt><dd>{desde(m.ultimo_contato)}</dd>
          </dl>
        </Cartao>

        <Cartao titulo="Sistema">
          <dl className="perfil">
            <dt>SO</dt><dd>{String(sis["so"] ?? "--")}</dd>
            <dt>Build</dt><dd>{String(sis["so_build"] ?? "--")}</dd>
            <dt>Arquitetura</dt><dd>{String(sis["arquitetura"] ?? "--")}</dd>
            <dt>CPU</dt><dd>{sis["cpu_nucleos"] ? `${sis["cpu_nucleos"]} nucleos` : "--"}</dd>
            <dt>Memoria</dt><dd>{gb(Number(sis["ram_mb"]))}</dd>
          </dl>
          {/* Razao contra limite -> medidor. E a metrica sobre a qual o alerta
              de disco baixo dispara: o painel tem que mostrar aquilo que alerta. */}
          {Number(sis["disco_total_mb"]) > 0 && (
            <div className="mt-lg">
              <Medidor rotulo="Disco usado"
                       usado={Number(sis["disco_total_mb"]) - Number(sis["disco_livre_mb"])}
                       total={Number(sis["disco_total_mb"])} unidade="MB"
                       nota={`${gb(Number(sis["disco_livre_mb"]))} livres`} />
            </div>
          )}
        </Cartao>

        <Cartao titulo="Aplicacao">
          <dl className="perfil">
            <dt>Versao</dt><dd>{String(app["versao"] ?? "--")}</dd>
            <dt>Produto</dt><dd>{String(app["produto"] ?? "--")}</dd>
            <dt>Instancias</dt>
            <dd>{instancias.length > 0 ? `${instancias.length}: ${instancias.join(", ")}` : "--"}</dd>
            <dt>Instancia ativa</dt><dd>{String(app["instancia_ativa"] ?? "--")}</dd>
            <dt>Uptime</dt><dd>{uptime(Number(app["uptime_s"]))}</dd>
          </dl>
        </Cartao>
      </div>

      {podeEscrever(usuario) && (
        <Cartao titulo="Acoes">
          <div className="grade grade--3">
            <form onSubmit={(ev) => {
              ev.preventDefault();
              const a = new FormData(ev.currentTarget).get("apelido") as string;
              void acao(() => api.post(`/maquinas/${id}/apelido`, { apelido: a }));
            }}>
              <label className="campo"><span className="campo__rotulo">Apelido</span>
                <input type="text" name="apelido" defaultValue={m.apelido}
                       placeholder="Balanca da portaria" /></label>
              <button className="botao botao--secundario botao--pequeno" type="submit">Salvar apelido</button>
            </form>

            {m.status === "ativa" && (
              <form onSubmit={(ev) => {
                ev.preventDefault();
                const mo = new FormData(ev.currentTarget).get("motivo") as string;
                void acao(() => api.post(`/maquinas/${id}/liberar`, { motivo: mo }));
              }}>
                <label className="campo"><span className="campo__rotulo">Liberar vaga</span>
                  <input type="text" name="motivo" defaultValue="troca de servidor" /></label>
                <button className="botao botao--secundario botao--pequeno" type="submit">Liberar vaga</button>
                <span className="campo__dica">Reversivel: a maquina volta se ativar com um codigo novo.</span>
              </form>
            )}

            {ehAdmin(usuario) && (
              <form onSubmit={(ev) => {
                ev.preventDefault();
                const mo = new FormData(ev.currentTarget).get("motivo") as string;
                void acao(() => api.post(`/maquinas/${id}/bloquear`, { motivo: mo }));
              }}>
                <label className="campo"><span className="campo__rotulo">Bloquear</span>
                  <input type="text" name="motivo" placeholder="Motivo" required /></label>
                <button className="botao botao--perigo botao--pequeno" type="submit">Bloquear maquina</button>
                <span className="campo__dica">Esta digital nao ativa mais. So em pirataria confirmada.</span>
              </form>
            )}
          </div>
        </Cartao>
      )}

      <div className="grade grade--2">
        <Cartao titulo="Presenca diaria" pergunta="180 dias, ate ontem">
          <FaixaPresenca dias={r.diario} />
        </Cartao>
        {/* Grafico SEPARADO da presenca: unidade e escala diferentes. Junta-los
            num eixo duplo inventaria uma correlacao que nao existe no dado. */}
        <Cartao titulo="Instancias no pico do dia" pergunta="contra o limite da licenca">
          <SerieInstancias dias={r.diario} limite={r.licenca.max_instancias} />
        </Cartao>
      </div>

      <Cartao titulo="Ultimos contatos"
              pergunta="clique numa linha para ver os campos que a tabela nao mostra">
        {r.historico.length === 0 ? <Vazio titulo="Nenhum heartbeat registrado" /> : (
          <div className="tabela-rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Quando</th><th>Resposta</th><th>Versao</th><th>Instancias</th>
                  <th>Instancia ativa</th><th>Disco livre</th><th>Ultimo erro</th><th>IP publico</th>
                </tr>
              </thead>
              <tbody>
                {r.historico.map((h) => (
                  <>
                    <tr key={h.id} onClick={() => setAberta(aberta === h.id ? null : h.id)}
                        style={{ cursor: "pointer" }}>
                      <td className="suave pequeno">{dataHora(h.recebido_em)}</td>
                      <td>
                        <span className={`selo selo--${h.acao === "ok" ? "ok" : "critico"}`}>{h.acao}</span>
                      </td>
                      <td className="suave pequeno">{h.versao_app}</td>
                      <td className="tabela__num">{h.instancias ?? "--"}</td>
                      <td className="pequeno">{h.instancia_ativa || "--"}</td>
                      <td className="tabela__num">{gb(h.disco_livre_mb)}</td>
                      <td className="tabela__erro" title={h.ultimo_erro}>{h.ultimo_erro}</td>
                      <td className="tabela__mono">{h.ip_publico || "--"}</td>
                    </tr>
                    {/* As 21 colunas do heartbeat ficam alcancaveis: oito
                        visiveis, o resto aqui. Encolher a linha para caber tudo
                        seria trocar legibilidade por densidade. */}
                    {aberta === h.id && (
                      <tr key={`${h.id}-detalhe`}>
                        <td colSpan={8} style={{ background: "var(--superficie-alt)" }}>
                          <dl className="perfil" style={{ gridTemplateColumns: "160px 1fr 160px 1fr" }}>
                            <dt>Uptime</dt><dd>{uptime(h.uptime_s)}</dd>
                            <dt>IP local</dt><dd>{h.ip_local || "--"}</dd>
                            <dt>Hostname</dt><dd>{h.hostname || "--"}</dd>
                            <dt>Usuario Windows</dt><dd>{h.usuario_win || "--"}</dd>
                            <dt>Dominio</dt><dd>{h.dominio || "--"}</dd>
                            <dt>Sistema</dt><dd>{h.so_versao || "--"}</dd>
                            <dt>Arquitetura</dt><dd>{h.arquitetura || "--"}</dd>
                            <dt>CPU / RAM</dt>
                            <dd>{h.cpu_nucleos ?? "--"} nucleos &middot; {gb(h.ram_mb)}</dd>
                          </dl>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </>
  );
}
